import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Loader2, PlayCircle, ShieldCheck, ShieldAlert } from "lucide-react";

type Request = {
  id: string;
  source: string;
  status: string;
  stage: string | null;
  progress: number;
  reason: string | null;
  latency_ms: number | null;
  error: string | null;
  created_at: string;
  finished_at: string | null;
};

const STAGE_LABEL: Record<string, string> = {
  queued: "Queued",
  scanning: "Running scanners",
  recording: "Recording snapshot",
  reconciling: "Reconciling findings",
  notifying: "Sending alerts",
  done: "Complete",
};

/**
 * Background "Run security scan" job: queues a rescan request, streams its
 * progress/stage over realtime, and calls onComplete() when the run finishes so
 * the page can refresh its findings automatically.
 */
export function SecurityScanJobPanel({ onComplete }: { onComplete: () => void }) {
  const [job, setJob] = useState<Request | null>(null);
  const [starting, setStarting] = useState(false);
  const completeRef = useRef(onComplete);
  completeRef.current = onComplete;
  const lastStatus = useRef<string | null>(null);

  const loadLatest = async () => {
    const { data } = await supabase
      .from("security_rescan_requests")
      .select("id, source, status, stage, progress, reason, latency_ms, error, created_at, finished_at")
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    setJob((data as Request) ?? null);
  };

  useEffect(() => {
    loadLatest();
    const channel = supabase
      .channel(`admin-security-scan-job:${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "security_rescan_requests" },
        () => loadLatest(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Auto-refresh the page data as soon as a run transitions into a final state.
  useEffect(() => {
    if (!job) return;
    const done = job.status === "ok" || job.status === "error" || job.status === "failed";
    if (done && lastStatus.current && lastStatus.current !== job.status) {
      completeRef.current();
      if (job.status === "ok") toast.success("Security scan finished — results refreshed");
      else toast.error(job.error ?? "Security scan failed");
    }
    lastStatus.current = job.status;
  }, [job]);

  const start = async () => {
    setStarting(true);
    try {
      const { data: requestId, error: rpcErr } = await supabase.rpc("request_security_rescan", {
        _reason: "Admin UI background scan",
      });
      if (rpcErr) throw rpcErr;
      await loadLatest();
      toast.info("Security scan queued");

      const {
        data: { session },
      } = await supabase.auth.getSession();
      // Fire and forget: the job row carries progress while this runs.
      supabase.functions
        .invoke("security-rescan", {
          body: {
            source: "manual",
            reason: "Admin UI background scan",
            request_id: requestId,
            findings: [],
          },
          headers: session ? { Authorization: `Bearer ${session.access_token}` } : undefined,
        })
        .then(({ error }) => {
          if (error) toast.error(error.message);
        });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not queue the scan");
    } finally {
      setStarting(false);
    }
  };

  const running = job ? job.status === "pending" || job.status === "running" : false;
  const pct = running ? Math.max(job?.progress ?? 0, 8) : job?.status === "ok" ? 100 : (job?.progress ?? 0);

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold">Background security scan</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Queues a scan that runs in the background. Progress updates live and findings refresh
            automatically when it completes.
          </p>
        </div>
        <Button size="sm" onClick={start} disabled={starting || running}>
          {starting || running ? (
            <Loader2 className="size-4 animate-spin mr-1" />
          ) : (
            <PlayCircle className="size-4 mr-1" />
          )}
          {running ? "Scan in progress…" : "Run security scan"}
        </Button>
      </div>

      {job && (
        <div className="mt-4 space-y-2" role="status" aria-live="polite">
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5 font-medium">
              {running ? (
                <Loader2 className="size-3.5 animate-spin text-primary" />
              ) : job.status === "ok" ? (
                <ShieldCheck className="size-3.5 text-primary" />
              ) : (
                <ShieldAlert className="size-3.5 text-destructive" />
              )}
              {STAGE_LABEL[job.stage ?? ""] ?? (running ? "Working" : job.status)}
            </span>
            <span className="font-mono text-[10px] text-muted-foreground">
              {job.source} · {new Date(job.created_at).toLocaleString()}
              {job.latency_ms != null ? ` · ${job.latency_ms}ms` : ""}
            </span>
          </div>
          <Progress value={pct} className="h-2" />
          {job.error && <p className="text-xs text-destructive">{job.error}</p>}
        </div>
      )}
    </div>
  );
}
